import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, ChatMessageView, Icon } from '@aidkitorg/component-library';
import { ConfigurationContext } from '../Context';
import { useMarkdown } from '../Util';
import { Root } from '@aidkitorg/types/lib/survey';
import { State } from '@aidkitorg/typesheets/lib/collab';
import { ChatConversationContext, useOpenAITextChat } from '../Contexts/ChatContext';
import slugid from 'slugid';
import { v4 } from 'uuid';
import { ChatMessageItem, ContentPart } from '@aidkitorg/types/lib/convoai/conversation';
import { ChatSessionContext } from '../Contexts/ChatSessionContext';

export default function CoachTab(props: { survey: Root, state: State }) {
  const [inputText, setInputText] = useState<string>("");
  const { messages, phase, dispatch: chatDispatch } = useContext(ChatConversationContext);
  const { sendMessage } = useOpenAITextChat();
  const config = useContext(ConfigurationContext);
  const session = useContext(ChatSessionContext);

  const updateContext = useCallback((key: string, ...value: ContentPart[]) => {
    sendMessage({
      type: 'update_item',
      id: key,
      with: {
        id: key,
        type: 'message',
        role: 'system',
        content: value,
        meta: {
          time: Date.now()
        }
      }
    });
  }, [sendMessage]);


  useEffect(() => {
    const instructions = `
    you are helping guide a distro author in understanding the impact of their changes as they are made.    
    when asked about their changes, relate them using plain language and keep responses short.
  `;
    updateContext(
      '__sys_msg_instr__',
      { type: 'text', content: instructions }
    );

  }, [])

  useEffect(() => {

    if (props.state.currentAuthor) {
      updateContext(
        'authorName',
        {
          type: 'text',
          content: `author name: ${props.state.currentAuthor.name || 'unknown'}`
        }
      );
    }

  }, [props.state.currentAuthor])

  useEffect(() => {
    if (props.state.unpublishedChanges) {
      updateContext(
        'unpublishedChanges',
        ...(props.state?.unpublishedChanges?.map(change => ({
          type: 'text',
          content: JSON.stringify({ unpublishedChange: change })
        }) as ContentPart) ?? [])
      );
    }
  }, [props.state.unpublishedChanges])


  useEffect(() => {
    updateContext(
      'survey',
      {
        type: 'text',
        content: JSON.stringify(props.survey)
      }
    );
  }, [props.survey]);

  const distroEditor = useMemo(() => document.querySelector('distro-editor')?.shadowRoot, [props.survey]);

  /* Distro is a Shadow DOM. This means that it is actually 
  *  impossible to interrogate the internal React Components from outside the distro-editor component.
  *
  *  Things like Context Providers simply don't get passed in, which would have made life WAY simpler.
  *
  *  Fortunately, this Shadow DOM is set to 'open', meaning we can at least interrogate the raw HTML Element objects.
  *
  *  To work around this, this callback employs a few things:
  *  - Shadow DOM is visible to the root document's query selector
  *  - Once we have a handle of the Shadow DOM, we can reach into it.
  *  - Every HTML Element within the Distro DOM is related to a Tracked Object. 
  *    As a result, we can get the Tracked Object's ID from the `data-tracker-id` field.
  *  - Once we have the Tracked Object ID, we can get the state of that object using the provided Distro State.
  */
  const indicateInterest = useCallback(async (e: MouseEvent) => {
    const elem = distroEditor?.elementFromPoint(e.x, e.y);

    if (!elem) return;

    let node: Element | null | undefined = elem;
    let trackerId: string | null | undefined = node?.getAttribute('data-tracker-id');
    while (!trackerId && node) {
      node = node.parentElement;
      trackerId = node?.getAttribute('data-tracker-id');
    }

    // nothing to do if we don't have a tracked object.
    if (!trackerId) {
      return;
    }

    const obj = props.state?.workingSet[trackerId];

    let content = obj?.read(true);

    if (!content) {
      return;
    }

    if (typeof content !== 'string') {
      content = JSON.stringify(content)
    }

    sendMessage({
      id: slugid.encode(v4()),
      type: 'message',
      role: 'user',
      content: [{ type: 'text', content: `tell me about this part of the survey: ${content}` }],
      meta: {
        time: Date.now()
      }
    });

  }, [distroEditor, props.state]);

  useEffect(() => {
    distroEditor?.addEventListener('dblclick', indicateInterest as any);
    return () => distroEditor?.removeEventListener('dblclick', indicateInterest as any);

  }, [distroEditor, indicateInterest]);

  function MessageContent({ message }: { message: ChatMessageItem }) {
    let content: string;
    switch (message.type) {
      case 'message':
        content = Array.isArray(message.content) ? message.content.map(c => c.type === 'text' ? c.content : '').join('\n\n') : message.content!;
        break;
      default:
        content = '';
        break;
    }
    return useMarkdown(content);
  }

  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    items.push({
      role: 'assistant',
      meta: {
        time: Date.now(),
      },
      content: <span>Try double-clicking on something, or asking about your pending changes!</span>,
    });

    items = items.concat(messages.map(m => {
      return {
        role: m.type === 'message' ? m.role : 'system',
        meta: { time: Date.now(), ...(m.meta ?? {}) },
        content: <MessageContent message={m} />
      }
    }) || []);

    return items;
  }, [messages]);

  const handleChatSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (inputText) {
      sendMessage({
        id: slugid.encode(v4()),
        type: 'message',
        role: 'user',
        content: [{
          type: 'text',
          content: inputText
        }]
      });
    }
    setInputText('');
  }

  const agentName = config?.capabilities?.aiCoach?.appearance?.name || "Luna";
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || "https://i.postimg.cc/1XcYwd4D/luna-cropped.png";

  return <div>
    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <span className="p-2 bg-indigo-300 rounded-full">{(config?.user?.name?.substring(0, 2)) ?? 'U'}</span>,
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />

    <div className="border-t border-gray-200 p-3">
      <span hidden={phase !== 'thinking'} className="text-center block w-full animate-pulse p-1">🧠</span>
      <div className="flex items-center gap-2">
        <form
          className="flex flex-1 gap-2"
          onSubmit={handleChatSubmit}>
          <input
            disabled={phase === 'thinking'}
            type="text"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Type a message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <Button
            type="submit"
            isDisabled={!inputText}
          >
            <Icon
              className="-rotate-45 -mt-1 -mr-1"
              name="PaperAirplaneIcon" />
          </Button>
          <Button
            type="reset"
            title="Restart Chat"
            onClick={() => {
              chatDispatch({ type: 'clear_chat' });
              session.clearSession();
            }}
          >
            <Icon
              name="FireIcon"
            />
          </Button>

        </form>
      </div>
    </div>
  </div>
}
